import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchFromPublicAPI, IS_SANDBOX_MODE } from '../services/common';
import {
  listInvoicePaymentMethods,
  chargeInvoiceWithSavedCardPublic,
  setInvoiceSaveOnPay,
  saveCardFromPayin,
  quoteSurcharge,
} from '../services/paymentsService';
import useGateToken from '../components/publicInvoice/useGateToken';
import DobGate from '../components/publicInvoice/DobGate';
import SavedCardList from '../components/publicInvoice/SavedCardList';
import AddPaymentMethod from '../components/publicInvoice/AddPaymentMethod';

// Helper function to get status styling for invoices
// Updated for Rainforest API v2024-10-16 statuses
const getStatusStyle = (status) => {
  const normalizedStatus = (status || 'pending').toLowerCase();
  
  // Define status categories and their colors
  const statusStyles = {
    // Pending/Waiting states - Yellow
    pending: { bg: '#fff3cd', color: '#856404', label: 'Pending' },
    
    // Success states - Green
    paid: { bg: '#d1e7dd', color: '#0f5132', label: 'Paid' },
    
    // Processing states - Blue
    authorized: { bg: '#cce5ff', color: '#004085', label: 'Authorized' },
    processing: { bg: '#cce5ff', color: '#004085', label: 'Processing' },
    
    // Warning states - Orange
    returned: { bg: '#ffe8cc', color: '#805300', label: 'Returned' },
    under_review: { bg: '#ffe8cc', color: '#805300', label: 'Under Review' },
    canceled: { bg: '#e2e3e5', color: '#383d41', label: 'Canceled' },
    
    // Error states - Red
    failed: { bg: '#f8d7da', color: '#721c24', label: 'Failed' },
    
    // Chargeback states - Various
    chargeback_inquiry_action_required: { bg: '#fff3cd', color: '#856404', label: 'Inquiry Required' },
    chargeback_inquiry_processing: { bg: '#cce5ff', color: '#004085', label: 'Inquiry Processing' },
    chargeback_inquiry_responded: { bg: '#cce5ff', color: '#004085', label: 'Inquiry Responded' },
    chargeback_dispute_action_required: { bg: '#f8d7da', color: '#721c24', label: 'Dispute Required' },
    chargeback_processing: { bg: '#cce5ff', color: '#004085', label: 'Chargeback Processing' },
    chargeback_prearbitration: { bg: '#ffe8cc', color: '#805300', label: 'Pre-Arbitration' },
    chargeback_arbitration: { bg: '#ffe8cc', color: '#805300', label: 'Arbitration' },
    chargeback_provisional_win: { bg: '#d1e7dd', color: '#0f5132', label: 'Provisional Win' },
    chargeback_lost: { bg: '#f8d7da', color: '#721c24', label: 'Chargeback Lost' },
    chargeback_won: { bg: '#d1e7dd', color: '#0f5132', label: 'Chargeback Won' },
  };
  
  // Return matching style or default to pending style
  return statusStyles[normalizedStatus] || { 
    bg: '#e2e3e5', 
    color: '#383d41', 
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1).replace(/_/g, ' ')
  };
};

// Responsive CSS styles injected into document
const responsiveStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .public-invoice-container {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
  }
  
  .public-invoice-card {
    max-width: 800px;
    margin: 0 auto;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    overflow: hidden;
  }
  
  .public-invoice-header {
    background-color: #1976d2;
    color: white;
    padding: 30px;
    text-align: center;
  }
  
  .public-invoice-header h1 {
    margin: 0 0 10px 0;
    font-size: 28px;
  }
  
  .public-invoice-content {
    padding: 30px;
  }
  
  .invoice-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
  }
  
  .line-items-table-header,
  .line-items-table-row {
    display: grid;
    grid-template-columns: 90px 80px 2fr 100px;
    gap: 10px;
    padding: 12px 15px;
  }
  
  .line-items-table-header {
    background-color: #f8f9fa;
    font-weight: bold;
    font-size: 13px;
    border-bottom: 1px solid #ddd;
  }
  
  .total-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;
  }
  
  .total-box {
    background-color: #e3f2fd;
    padding: 20px;
    border-radius: 8px;
    min-width: 200px;
    text-align: center;
  }
  
  .payment-section {
    background-color: #f8f9fa;
    padding: 30px;
    border-radius: 8px;
    margin-bottom: 20px;
  }
  
  .payment-container {
    min-height: 400px;
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    border: 1px solid #ddd;
  }
  
  /* Mobile-specific line items display */
  .line-items-mobile {
    display: none;
  }
  
  .line-items-desktop {
    display: block;
  }
  
  /* Mobile Responsive Styles */
  @media (max-width: 640px) {
    .public-invoice-container {
      padding: 12px;
    }
    
    .public-invoice-card {
      border-radius: 12px;
    }
    
    .public-invoice-header {
      padding: 24px 16px;
    }
    
    .public-invoice-header h1 {
      font-size: 22px;
    }
    
    .public-invoice-content {
      padding: 20px 16px;
    }
    
    .invoice-info-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
    
    /* Hide desktop table, show mobile cards */
    .line-items-desktop {
      display: none;
    }
    
    .line-items-mobile {
      display: block;
    }
    
    .line-item-card {
      background-color: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;
    }
    
    .line-item-card:last-child {
      margin-bottom: 0;
    }
    
    .line-item-description {
      font-weight: 600;
      font-size: 15px;
      margin-bottom: 12px;
      color: #333;
    }
    
    .line-item-details {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      color: #666;
    }
    
    .line-item-subtotal {
      font-weight: 600;
      color: #1976d2;
    }
    
    .total-container {
      justify-content: center;
    }
    
    .total-box {
      min-width: unset;
      width: 100%;
      padding: 16px;
    }
    
    .payment-section {
      padding: 20px 16px;
    }
    
    .payment-container {
      padding: 16px;
      min-height: 350px;
    }
    
    .status-message-box {
      padding: 16px;
    }
    
    .footer-message {
      padding: 16px;
    }
  }
  
  /* Extra small screens */
  @media (max-width: 380px) {
    .public-invoice-container {
      padding: 8px;
    }
    
    .public-invoice-header {
      padding: 20px 12px;
    }
    
    .public-invoice-header h1 {
      font-size: 20px;
    }
    
    .public-invoice-content {
      padding: 16px 12px;
    }
    
    .total-box .total-amount {
      font-size: 22px;
    }
  }
`;

const PublicInvoice = () => {
  const { hash } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionKey, setSessionKey] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'declined', 'error'
  // Tracks whether the Rainforest <rainforest-payment> widget has finished
  // mounting its iframe. Used to hide the "Loading payment form..." copy.
  const [paymentFormLoaded, setPaymentFormLoaded] = useState(false);
  const paymentComponentRef = useRef(null);

  // -------------------------------------------------------------------
  // Credit-card surcharging (Option-2 UX: upfront notice + post-BIN
  // dynamic update).
  //
  // The flow is:
  //   1. Server includes ``invoice.surchargeNotice`` only for practices
  //      with surcharging enabled. Presence of that field is what
  //      activates everything in this section — non-surcharging
  //      practices see zero behavioural change vs. the prior page.
  //   2. We render an upfront "credit cards add up to X% surcharge"
  //      banner BEFORE the patient touches the form, then mount the
  //      Payment Component with ``hide-button`` so we can intercept
  //      submission and replace the upstream payin_config_id mid-flight.
  //   3. Whenever the Component fires ``bin-lookup`` we POST it to
  //      /quote-surcharge. The server may mint a replacement
  //      payin_config; on a swap we ``setAttribute('payin-config-id', …)``
  //      and wait for ``config-update-accepted`` before re-enabling
  //      submit (so the patient can never click pay against a stale
  //      config that's about to be replaced).
  //   4. The custom submit button calls ``component.submit()``.
  //
  // ``currentPayinConfigId`` mirrors what the Component is currently
  // pointed at. We update it in state so React renders the latest
  // breakdown next to the form, but we ALSO call setAttribute on the
  // DOM node directly because Rainforest re-mounts its iframe lazily
  // when the attribute changes — going through a React re-render works
  // too but adds a frame of jank.
  // -------------------------------------------------------------------
  // Surcharging mode requires BOTH the practice opt-in (surfaced via
  // ``surchargeNotice``) AND a payin_config that was minted with
  // ``allow_bin_lookup: true`` (surfaced via ``binLookupEnabled``).
  // Without the latter Rainforest will never fire ``bin-lookup`` events
  // and the dynamic-pricing UI would show a stale "Updating total…"
  // forever. The server self-heals legacy configs in get_public_invoice
  // by minting a replacement, so this should always be true for
  // surcharging practices — but if for some reason it isn't, we fall
  // back to the standard non-surcharging UX rather than presenting a
  // broken dynamic price the patient can't trigger.
  const surchargingMode = !!invoice?.surchargeNotice && invoice?.binLookupEnabled !== false;
  const [currentPayinConfigId, setCurrentPayinConfigId] = useState(null);
  const [surchargeQuote, setSurchargeQuote] = useState(null);   // last quote result
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  // True after the FIRST successful bin-lookup → /quote-surcharge
  // round-trip, regardless of whether that round-trip produced a
  // surcharge. We need this separate from ``surchargeQuote`` because
  // a debit card legitimately returns ``surcharge: null`` — without
  // this flag we couldn't distinguish "patient hasn't entered a card
  // yet" from "patient entered a debit card and there's no surcharge",
  // and the upfront yellow "may apply" banner would stay visible
  // forever after a debit/HSA card was typed.
  const [quoteReceived, setQuoteReceived] = useState(false);
  // True while Rainforest is processing a config swap. We disable the
  // custom submit button until ``config-update-accepted`` fires so the
  // patient can't pay against a config that's mid-replacement.
  const [configSwapPending, setConfigSwapPending] = useState(false);
  // Sync the local config id with whatever the loaded invoice carries.
  // The bin-lookup handler will overwrite this with replacement ids
  // returned from /quote-surcharge.
  useEffect(() => {
    if (invoice?.payinConfigId) {
      setCurrentPayinConfigId(invoice.payinConfigId);
    }
  }, [invoice?.payinConfigId]);

  // -------------------------------------------------------------------
  // DOB gate + saved cards
  //
  // The patient can opt in to saved-card management on this invoice by
  // verifying their date of birth against KollaContacts. Once verified,
  // we hold a short-lived gate token in sessionStorage (via useGateToken)
  // and unlock the SavedCardList / AddPaymentMethod widgets above the
  // existing one-time payment form.
  //
  // gateView state machine:
  //   'idle'  → CTA shown ("Use a saved card on this invoice")
  //   'dob'   → DobGate visible
  //   'list'  → SavedCardList visible (we have a valid gateToken)
  //   'add'   → AddPaymentMethod visible
  //   'unavailable' → DOB lookup failed (KollaContacts has no birth_date
  //                   for this patient). We hide the entry CTA and let
  //                   the existing guest-pay flow proceed unchanged so
  //                   the patient never sees a confusing dead end.
  // -------------------------------------------------------------------
  const { gateToken, setGateToken, gateMeta, setGateMeta } = useGateToken(hash);
  // Initial view: if we already have a gate token in sessionStorage (the
  // patient verified DOB earlier this tab session), land on the saved-card
  // list so a refresh doesn't hide the management UI behind the entry CTA
  // again. Otherwise we start at the entry CTA.
  const [gateView, setGateView] = useState(() => (gateToken ? 'list' : 'idle'));
  const [savedMethods, setSavedMethods] = useState([]);
  const [loadingSavedMethods, setLoadingSavedMethods] = useState(false);
  const [savedMethodsError, setSavedMethodsError] = useState(null);
  const [makeNewCardDefault, setMakeNewCardDefault] = useState(false);
  const [gateUnavailable, setGateUnavailable] = useState(false);

  // -------------------------------------------------------------------
  // Opportunistic save (guest-pay path)
  //
  // When the patient hasn't verified their DOB they can still type a card
  // into the main payin form. We default the "save my card for next
  // time" checkbox to ``true`` because that's what most patients want
  // (matches retail checkout norms) — and the backend creates payin_configs
  // with ``savePaymentMethod=true`` to match. If the patient un-checks
  // we PATCH the upstream config; the Rainforest payin webhook then
  // reads the flag and ``vault_upsert_from_payin`` honours it.
  //
  // ``saveCardSyncing`` is used purely to disable the checkbox during the
  // round-trip so we don't get an inconsistent UI if the request fails.
  // -------------------------------------------------------------------
  const [saveCardOnPay, setSaveCardOnPay] = useState(true);
  const [saveCardSyncing, setSaveCardSyncing] = useState(false);
  const [saveCardError, setSaveCardError] = useState(null);
  // Mirror saveCardOnPay into a ref so the rainforest-payment `approved`
  // listener (which is registered once per invoice/session) always reads
  // the latest checkbox value without us having to re-subscribe — and
  // tear down a working iframe — every time the patient toggles it.
  const saveCardOnPayRef = useRef(saveCardOnPay);
  useEffect(() => {
    saveCardOnPayRef.current = saveCardOnPay;
  }, [saveCardOnPay]);

  const handleToggleSaveOnPay = useCallback(async (e) => {
    const next = e.target.checked;
    const prev = saveCardOnPay;
    setSaveCardOnPay(next);
    setSaveCardError(null);
    setSaveCardSyncing(true);
    try {
      const res = await setInvoiceSaveOnPay(hash, next);
      if (!res.ok) {
        // Roll back so the checkbox reflects what's actually on the
        // upstream config — keeps the user's mental model honest.
        setSaveCardOnPay(prev);
        setSaveCardError(
          res?.data?.error || 'Could not update save preference. Please try again.',
        );
      }
    } catch (err) {
      setSaveCardOnPay(prev);
      setSaveCardError('Could not update save preference. Please try again.');
    } finally {
      setSaveCardSyncing(false);
    }
  }, [hash, saveCardOnPay]);

  const isDemo = new URLSearchParams(window.location.search).get('demo') === 'true';
  // `?showOriginal=true` is a staff-only flag: when present, the API
  // disables superseded-chain resolution and returns the actual
  // invoice for this hash even if it has been replaced. Patients
  // never see this flag — the office manager pastes the augmented
  // URL when auditing what was originally sent.
  const showOriginal = new URLSearchParams(window.location.search).get('showOriginal') === 'true';

  // Load Rainforest JS bundles. Loads sandbox.* bundles when either
  // (a) the global IS_SANDBOX_MODE switch is on (mirrors the backend's
  // RAINFOREST_BASE_URL), or (b) this specific patient invoice was opened
  // with ?demo=true (one-off override that still works in production mode).
  useEffect(() => {
    const prefix = (IS_SANDBOX_MODE || isDemo) ? 'sandbox.' : '';
    const scripts = [`${prefix}merchant.js`, `${prefix}payment.js`];
    const loaded = [];

    scripts.forEach((name) => {
      const id = `rainforest-${name}`;
      if (!document.getElementById(id)) {
        const el = document.createElement('script');
        el.id = id;
        el.type = 'module';
        el.src = `https://static.rainforestpay.com/${name}`;
        document.head.appendChild(el);
        loaded.push(el);
      }
    });

    return () => loaded.forEach((el) => el.remove());
  }, [isDemo]);
  
  // Inject responsive styles on mount
  useEffect(() => {
    const styleId = 'public-invoice-styles';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = responsiveStyles;
      document.head.appendChild(styleEl);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Parse a date string into a Date *without* introducing a timezone shift
  // for bare YYYY-MM-DD inputs. JS's `new Date('2026-04-07')` is parsed as
  // UTC midnight, which then renders as Apr 6 anywhere west of UTC. Any
  // string that isn't a bare calendar date (i.e. has a T or timezone) is
  // delegated to the standard parser, which is the right behavior for
  // full ISO timestamps.
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (ymd) {
      return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    }
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = parseDateLocal(dateString);
    if (!d) return 'Invalid Date';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Short "Apr 7" rendering used inside line-item rows.
  const formatLineItemDate = (dateString) => {
    if (!dateString) return '-';
    const d = parseDateLocal(dateString);
    if (!d) return '-';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };


  // Load invoice data - only after captcha is verified
  useEffect(() => {
    const loadInvoice = async () => {
      if (!hash) {
        setError('Invalid invoice link');
        setLoading(false);
        return;
      }

      // Wait for captcha verification before loading invoice
      if (!captchaVerified || !captchaToken) {
        return;
      }

      try {
        const queryString = showOriginal ? '?showOriginal=true' : '';
        const data = await fetchFromPublicAPI(
          `/api/payments/public/invoice/${hash}${queryString}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              captchaToken: captchaToken
            })
          }
        );

        if (data && data.success) {
          setInvoice(data.invoice);
        } else {
          setError(data?.error || 'Invoice not found');
        }
      } catch (err) {
        console.error('Error loading invoice:', err);
        setError('Failed to load invoice. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    loadInvoice();
  }, [hash, captchaVerified, captchaToken, showOriginal]);

  // Load session for payment - separate from invoice to generate fresh sessions
  useEffect(() => {
    const loadSession = async () => {
      // Only load session if invoice is loaded, pending, and has payment config
      if (!invoice || invoice.status !== 'pending' || !invoice.payinConfigId) {
        return;
      }

      try {
        setSessionLoading(true);
        const queryString = showOriginal ? '?showOriginal=true' : '';
        const data = await fetchFromPublicAPI(`/api/payments/get-invoice-session/${hash}${queryString}`);

        if (data && data.success) {
          setSessionKey(data.session_key);
          console.log('Session created successfully');
        } else {
          console.error('Failed to create session:', data?.error);
          setError(data?.error || 'Failed to load payment form');
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load payment form. Please refresh the page.');
      } finally {
        setSessionLoading(false);
      }
    };

    loadSession();
  }, [invoice, hash, showOriginal]);

  // Execute reCAPTCHA v3 when component mounts
  useEffect(() => {
    const executeRecaptcha = () => {
      try {
        // Wait for grecaptcha to be ready
        if (!window.grecaptcha) {
          console.log('Waiting for grecaptcha to load...');
          setTimeout(executeRecaptcha, 100);
          return;
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha.execute('6LeEbOkrAAAAAJvzZzXa_3XbRJXTzvVVQ16uU813', {
            action: 'view_invoice'
          })
          .then((token) => {
            console.log('reCAPTCHA v3 token generated');
            setCaptchaToken(token);
            setCaptchaVerified(true);
            setCaptchaError(null);
          })
          .catch((err) => {
            console.error('Error executing reCAPTCHA:', err);
            setCaptchaError('Security verification failed. Please refresh the page.');
          });
        });
      } catch (err) {
        console.error('Error with reCAPTCHA:', err);
        setCaptchaError('Security verification failed. Please refresh the page.');
      }
    };

    executeRecaptcha();
  }, []);


  // Reset the "form loaded" flag whenever a new session is issued, so the
  // loading copy shows again on retry / re-mount.
  useEffect(() => {
    setPaymentFormLoaded(false);
  }, [sessionKey]);

  // Load saved cards whenever we have a fresh gate token. Pure read with
  // no side effects so it's safe to re-run after add/delete via the
  // `loadSavedMethods` ref-callback further down.
  const loadSavedMethods = useCallback(async () => {
    if (!gateToken || !hash) return;
    setLoadingSavedMethods(true);
    setSavedMethodsError(null);
    try {
      const { ok, status, data } = await listInvoicePaymentMethods(hash, gateToken);
      if (!ok) {
        // 401 means the gate token expired — drop it so the patient can
        // re-verify rather than staring at an empty list.
        if (status === 401) {
          setGateToken(null);
          setGateMeta(null);
          setGateView('dob');
          return;
        }
        setSavedMethodsError(data?.error || 'Could not load saved cards.');
        return;
      }
      setSavedMethods(data?.paymentMethods || []);
      // Intentionally NOT touching gateView here. The "first landing"
      // routing (idle → 'add' for empty, 'list' for non-empty) happens
      // exactly once in handleDobVerified using the savedCount from the
      // verify response. If we re-routed on every refresh we'd ping-pong
      // the user back into 'add' the moment they cancel out of it from
      // an empty list.
    } finally {
      setLoadingSavedMethods(false);
    }
  }, [hash, gateToken, setGateToken, setGateMeta]);

  useEffect(() => {
    if (gateToken) {
      loadSavedMethods();
    } else if (gateView === 'list' || gateView === 'add') {
      // Lost the token mid-flight (e.g. server returned 401) — reset
      // back to the entry point so the user can re-verify.
      setGateView('idle');
    }
  }, [gateToken, gateView, loadSavedMethods]);

  const handleDobVerified = useCallback(({ gateToken: token, firstName, hasCards, savedCount }) => {
    setGateToken(token);
    setGateMeta({ firstName, hasCards, savedCount });
    // One-shot landing routing: empty wallets go straight to the add
    // form (saves a click), wallets with at least one card land on the
    // list. After this, the view is owned by the user (cancel buttons,
    // "+ Add new card", etc.) — list refreshes never re-route.
    setGateView((savedCount || 0) === 0 ? 'add' : 'list');
  }, [setGateToken, setGateMeta, setGateView]);

  const handleDobUnavailable = useCallback(() => {
    // KollaContacts has no birth_date for this patient — we hide the
    // entire saved-card flow and let the existing guest-pay UI handle
    // checkout. Persisted in state (not just by closing the gate) so a
    // re-render doesn't bring the entry CTA back.
    setGateUnavailable(true);
    setGateView('idle');
  }, []);

  const handleAddCardSaved = useCallback(() => {
    setMakeNewCardDefault(false);
    // loadSavedMethods preserves the current 'add' view by design (so a
    // background refresh while the patient is mid-form doesn't yank them
    // out of it); after a successful save we explicitly want to flip
    // back to the list so they can see (and use) the card they just added.
    setGateView('list');
    loadSavedMethods();
  }, [loadSavedMethods]);

  // Public 'pay with this saved card' flow. We POST to /charge-saved
  // and immediately flip to the success screen — the actual paid status
  // arrives via the same payin webhook the live Rainforest widget relies
  // on, so no extra polling here. Errors surface inline so the patient
  // can pick a different card or fall back to the guest-pay form below.
  const [chargingVaultId, setChargingVaultId] = useState(null);
  const [chargeError, setChargeError] = useState(null);

  const handleChargeSavedCard = useCallback(async (method) => {
    if (!method?.id || !gateToken) return;
    setChargingVaultId(method.id);
    setChargeError(null);
    try {
      const { ok, data } = await chargeInvoiceWithSavedCardPublic(
        hash,
        gateToken,
        method.id,
      );
      if (!ok || !data?.success) {
        setChargeError(
          data?.error || 'We could not process that card. Please try another payment method.'
        );
        return;
      }
      // 202 Accepted — Rainforest will report the final status via
      // webhook, but we already have positive confirmation that the
      // charge was submitted. The "Payment Successful" screen mirrors
      // the existing widget UX.
      setPaymentComplete(true);
      setPaymentStatus('success');
    } finally {
      setChargingVaultId(null);
    }
  }, [hash, gateToken]);

  // Wire up Rainforest <rainforest-payment> event listeners.
  //
  // Previously this used `setTimeout(..., 100)` + `document.querySelector` to
  // find the element and then hid the "Loading payment form..." copy via
  // direct DOM mutation. That was racy: if the widget fired its `loaded`
  // event before the 100ms delay fired (or before the listener attached),
  // the copy was never hidden, and any React re-render also resurrected it.
  //
  // Now we attach via the React ref (so the listener is in place the
  // instant the element is mounted), drive visibility via React state,
  // and add a 6s safety net in case Rainforest never fires `loaded` — the
  // widget's own iframe will be visible by then anyway, and stale loading
  // copy is worse than a missed analytics signal.
  useEffect(() => {
    if (!(invoice &&
          invoice.status === 'pending' &&
          sessionKey &&
          (invoice.paymentMethodConfigId || invoice.payinConfigId))) {
      return;
    }

    const comp = paymentComponentRef.current;
    if (!comp) return;

    const handleLoaded = (e) => {
      // Rainforest dispatches `event.detail` as a single-element array
      // wrapping the LoadedPayload object documented at
      // https://docs.rainforestpay.com/docs/enable-paypal#event-listeners
      // — surface the per-method support flags so we can debug why
      // PayPal / Venmo / Pay Later buttons aren't showing without
      // attaching a debugger.
      const detail = Array.isArray(e?.detail) ? e.detail[0] : e?.detail;
      console.log('Payment component loaded successfully', {
        surchargingMode,
        practiceFlags: {
          paypalWalletEnabled: invoice?.paypalWalletEnabled,
          venmoEnabled: invoice?.venmoEnabled,
          paypalPayLaterEnabled: invoice?.paypalPayLaterEnabled,
        },
        merchantId: invoice?.merchantId,
        apple_pay: detail?.apple_pay,
        paypal_wallet: detail?.paypal_wallet,
        venmo: detail?.venmo,
        paypal_paylater: detail?.paypal_paylater,
        validated_ach: detail?.validated_ach,
        rawDetail: detail,
      });
      setPaymentFormLoaded(true);
    };
    const handleApproved = (e) => {
      console.log('Payment approved:', e);
      // Tear down the in-flight indicator before flipping to the success
      // screen — keeps the watchdog from firing later and harmlessly
      // calling setState on an unmounted-but-not-yet-collected component.
      clearSubmitInFlight();
      setPaymentComplete(true);
      setPaymentStatus('success');

      // Synchronous opportunistic save. Webhooks will also vault this
      // card via vault_upsert_from_payin once `payin.succeeded` arrives,
      // but webhook delivery has latency (and is broken in dev when the
      // merchant's webhook URL doesn't point at the local server). Doing
      // the save here as well means the next page load shows the card
      // immediately, and the subsequent webhook becomes a no-op upsert.
      if (saveCardOnPayRef.current) {
        try {
          // Rainforest dispatches `event.detail` as a one-element array
          // wrapping the API-style envelope (same shape as the save-only
          // tokenization flow we already handle in AddPaymentMethod):
          //   [{ status: 'SUCCESS', data: { payment_method_id, ... } }]
          const rawDetail = e?.detail;
          const envelope = Array.isArray(rawDetail) ? rawDetail[0] : rawDetail;
          const data = (envelope && envelope.data) || envelope || {};
          const paymentMethodId =
            data.payment_method_id
            || data.paymentMethodId
            || data.payment_method?.id
            || data.id;
          if (paymentMethodId) {
            saveCardFromPayin(hash, paymentMethodId).then((res) => {
              if (!res?.ok) {
                console.warn(
                  '[saveCardFromPayin] backend rejected save',
                  res?.status, res?.data,
                );
              }
            }).catch((err) => {
              console.warn('[saveCardFromPayin] network error', err);
            });
          } else {
            console.warn(
              '[saveCardFromPayin] approved event had no payment_method_id; '
              + 'webhook-driven save will still run when payin.succeeded arrives',
              rawDetail,
            );
          }
        } catch (err) {
          console.warn('[saveCardFromPayin] handler error', err);
        }
      }
    };
    const handleDeclined = (e) => {
      console.log('Payment declined:', e);
      clearSubmitInFlight();
      setPaymentComplete(true);
      setPaymentStatus('declined');
    };
    const handleError = (e) => {
      console.log('Payment error:', e);
      clearSubmitInFlight();
      setPaymentComplete(true);
      setPaymentStatus('error');
      // If we never got a `loaded` event before the error, still hide the
      // loading copy — the user shouldn't be staring at "Loading…" forever.
      setPaymentFormLoaded(true);
    };
    const handleAttempted = (e) => {
      console.log('Payment attempted:', e);
    };

    // Rainforest fires ``bin-lookup`` once the patient has typed enough
    // of the PAN for the BIN-lookup service to classify it. We POST the
    // payload to /quote-surcharge; the server decides whether a config
    // swap is needed and returns the (possibly new) payin_config_id.
    //
    // This handler is registered ONLY when surchargingMode is true —
    // for non-surcharging practices the Payment Component never emits
    // bin-lookup events anyway (we skip ``allow_bin_lookup`` on the
    // upstream config) so the listener would just be dead weight.
    const handleBinLookup = async (e) => {
      // Always log — even when surcharging mode is off — so we can
      // tell from the console whether Rainforest's BIN-lookup is
      // actually firing for this account/config. A common failure
      // mode is "I see the upfront banner but the price never
      // updates" which is almost always either (a) the bound
      // payin_config was created with allow_bin_lookup=false (server
      // self-heals this in get_public_invoice on the next load), or
      // (b) the Rainforest platform hasn't enabled BIN-lookup for
      // this merchant application yet (requires Rainforest support).
      console.log('[bin-lookup] event fired', e?.detail);
      if (!surchargingMode) return;
      try {
        const rawDetail = e?.detail;
        const envelope = Array.isArray(rawDetail) ? rawDetail[0] : rawDetail;
        const data = (envelope && envelope.data) || envelope || {};
        // Strip nothing — the server's compute_surcharge handles
        // missing fields defensively. Forwarding the raw payload also
        // means future Rainforest enrichments (e.g. richer healthcare
        // signals) start working without a frontend deploy.
        setQuoteLoading(true);
        setQuoteError(null);
        const res = await quoteSurcharge(hash, data);
        if (!res.ok || !res.data?.success) {
          setQuoteError(
            res.data?.error
            || 'Unable to refresh payment total. You can still submit this payment as-is.',
          );
          return;
        }
        const { surcharge, payinConfigId, changed } = res.data;
        setSurchargeQuote(surcharge || null);
        // Mark that a card has been classified — this swaps the UI
        // from the upfront "credit-card surcharge MAY apply" banner
        // to a definitive result panel (either the surcharge
        // breakdown or a "no surcharge applies" confirmation).
        setQuoteReceived(true);
        // Read via ref — this listener is registered once and we don't
        // want to re-subscribe (and tear down the iframe) every swap.
        const liveConfigId = currentPayinConfigIdRef.current;
        if (changed && payinConfigId && payinConfigId !== liveConfigId) {
          // Block submit until the Component confirms the swap. If
          // ``config-update-accepted`` never fires (network glitch),
          // the safety timeout below releases the lock so the
          // patient is never permanently stuck.
          setConfigSwapPending(true);
          setCurrentPayinConfigId(payinConfigId);
          if (paymentComponentRef.current) {
            paymentComponentRef.current.setAttribute(
              'payin-config-id',
              payinConfigId,
            );
          }
        }
      } catch (err) {
        console.warn('[bin-lookup] quote failed', err);
        setQuoteError(
          'Unable to refresh payment total. You can still submit this payment as-is.',
        );
      } finally {
        setQuoteLoading(false);
      }
    };

    // Fired by Rainforest after a successful payin-config-id swap
    // (i.e. our setAttribute call has been re-validated and the iframe
    // is now bound to the replacement config). Once we see this we
    // know the next ``component.submit()`` will charge the right amount.
    const handleConfigUpdateAccepted = () => {
      setConfigSwapPending(false);
    };

    comp.addEventListener('loaded', handleLoaded);
    comp.addEventListener('approved', handleApproved);
    comp.addEventListener('declined', handleDeclined);
    comp.addEventListener('error', handleError);
    comp.addEventListener('attempted', handleAttempted);
    comp.addEventListener('bin-lookup', handleBinLookup);
    comp.addEventListener('config-update-accepted', handleConfigUpdateAccepted);

    // Safety net: hide the loading copy after 6s no matter what. Rainforest
    // occasionally swallows `loaded` (observed in the wild on slow mobile
    // networks and after a cached iframe reload).
    const safetyTimeout = setTimeout(() => {
      setPaymentFormLoaded(true);
    }, 6000);

    return () => {
      clearTimeout(safetyTimeout);
      comp.removeEventListener('loaded', handleLoaded);
      comp.removeEventListener('approved', handleApproved);
      comp.removeEventListener('declined', handleDeclined);
      comp.removeEventListener('error', handleError);
      comp.removeEventListener('attempted', handleAttempted);
      comp.removeEventListener('bin-lookup', handleBinLookup);
      comp.removeEventListener('config-update-accepted', handleConfigUpdateAccepted);
    };
    // currentPayinConfigId is intentionally NOT in the dep array — we
    // read the latest value via closure inside handleBinLookup at call
    // time, but re-subscribing every config swap would tear down a
    // working iframe and lose user-entered card data. The ref below
    // keeps the read consistent without forcing re-subscribes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, sessionKey, hash, surchargingMode]);

  // Mirror currentPayinConfigId into a ref so handleBinLookup (registered
  // once per session) reads the latest value when comparing against the
  // server's response, without us re-subscribing every swap.
  const currentPayinConfigIdRef = useRef(null);
  useEffect(() => {
    currentPayinConfigIdRef.current = currentPayinConfigId;
  }, [currentPayinConfigId]);

  // ---------------------------------------------------------------------
  // Submit-in-flight state.
  //
  // The patient clicks "Pay $X" and Rainforest takes anywhere from a few
  // hundred ms to several seconds to:
  //   1. Tokenise the card client-side.
  //   2. POST it to Rainforest's gateway.
  //   3. (Optional) bounce them through a 3DS challenge.
  //   4. Fire ``approved`` / ``declined`` / ``error``.
  //
  // Without an in-flight indicator the button looked dead during that
  // window and a fast double-click could fire ``component.submit()``
  // twice — Rainforest typically 409s the second attempt, but the
  // patient sees a confusing error and the merchant gets two payin
  // records to reconcile.
  //
  // We guard with both a state (drives UI) and a ref (synchronous,
  // never stale across batched updates — the React state update from
  // the first click hasn't committed yet when the second click fires
  // 50ms later, so a ``submitInFlight``-only guard would miss it).
  //
  // Cleared on ``declined``/``error`` so a future "retry without
  // reload" screen would work, and via a 60s watchdog in case
  // Rainforest never fires anything (network drop, ad-blocker, the
  // gateway swallowing the request, etc.). 60s is comfortably longer
  // than the slowest legitimate 3DS challenge but short enough that
  // a patient who's been staring at "Processing…" for a minute can
  // re-click rather than reload the whole page.
  // ---------------------------------------------------------------------
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const submitInFlightRef = useRef(false);
  const submitWatchdogRef = useRef(null);
  const clearSubmitInFlight = useCallback(() => {
    submitInFlightRef.current = false;
    setSubmitInFlight(false);
    if (submitWatchdogRef.current) {
      clearTimeout(submitWatchdogRef.current);
      submitWatchdogRef.current = null;
    }
  }, []);
  // Don't leak the watchdog timer on unmount.
  useEffect(() => () => {
    if (submitWatchdogRef.current) clearTimeout(submitWatchdogRef.current);
  }, []);

  // Custom submit handler used when ``hide-button`` is on (i.e. in
  // surcharging mode). The Rainforest Payment Component exposes a
  // ``submit()`` method that triggers the same internal flow as the
  // built-in button. Disabled while a config swap is in flight so the
  // patient can never click against a stale config, and while a prior
  // submit is still pending so a fast second click can't fire two
  // payin attempts.
  const handleCustomSubmit = useCallback(() => {
    const comp = paymentComponentRef.current;
    if (!comp) return;
    if (configSwapPending || quoteLoading) return;
    // Synchronous double-click guard. The ``disabled`` attribute on
    // the button blocks browser-level repeats too, but we belt-and-
    // braces it with a ref so the React render lag between click 1
    // and click 2 can't sneak a duplicate through.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitInFlight(true);
    submitWatchdogRef.current = setTimeout(() => {
      console.warn(
        '[handleCustomSubmit] watchdog tripped: no Rainforest event in 60s, '
        + 're-enabling pay button',
      );
      clearSubmitInFlight();
    }, 60_000);
    try {
      if (typeof comp.submit === 'function') {
        comp.submit();
      } else if (typeof comp.pay === 'function') {
        // Older bundles expose ``pay()``; try as a defensive fallback.
        comp.pay();
      } else {
        console.warn('[handleCustomSubmit] Payment Component has no submit() method');
        // Nothing actually got submitted — re-enable the button so the
        // patient isn't stuck staring at a forever-spinning UI.
        clearSubmitInFlight();
      }
    } catch (err) {
      console.warn('[handleCustomSubmit] submit failed', err);
      clearSubmitInFlight();
    }
  }, [configSwapPending, quoteLoading, clearSubmitInFlight]);

  // Show verification screen while captcha is being processed
  if (!captchaVerified) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(24px, 6vw, 40px)',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%',
          margin: '0 auto'
        }}>
          {!captchaError ? (
            <>
              <div style={{ 
                fontSize: 'clamp(20px, 5vw, 24px)', 
                fontWeight: 'bold',
                color: '#1976d2', 
                marginBottom: '16px' 
              }}>
                Security Verification
              </div>
              <div style={{ 
                fontSize: 'clamp(14px, 4vw, 16px)', 
                color: '#666',
                marginBottom: '24px' 
              }}>
                Verifying your request...
              </div>
              <div style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{
                fontSize: '12px',
                color: '#999',
                marginTop: '20px',
                lineHeight: '1.5'
              }}>
                This verification helps protect against automated access.
              </div>
            </>
          ) : (
            <>
              <div style={{ 
                fontSize: 'clamp(20px, 5vw, 24px)', 
                fontWeight: 'bold',
                color: '#dc3545', 
                marginBottom: '16px' 
              }}>
                Verification Failed
              </div>
              <div style={{
                color: '#dc3545',
                fontSize: 'clamp(14px, 4vw, 16px)',
                padding: '15px',
                backgroundColor: '#f8d7da',
                borderRadius: '8px',
                border: '1px solid #f5c2c7',
                marginBottom: '20px',
                lineHeight: '1.5'
              }}>
                {captchaError}
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  width: '100%',
                  maxWidth: '200px'
                }}
              >
                Refresh Page
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(24px, 6vw, 40px)',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          width: '100%',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #1976d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }} />
          <div style={{ fontSize: 'clamp(14px, 4vw, 18px)', color: '#666' }}>Loading invoice...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(24px, 6vw, 40px)',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%'
        }}>
          <div style={{ fontSize: 'clamp(20px, 5vw, 24px)', color: '#dc3545', marginBottom: '12px' }}>
            Invoice Not Found
          </div>
          <div style={{ fontSize: 'clamp(14px, 4vw, 16px)', color: '#666', lineHeight: '1.5' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Show payment completion screen
  if (paymentComplete) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(30px, 8vw, 60px) clamp(20px, 5vw, 40px)',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '600px',
          width: '100%'
        }}>
          {paymentStatus === 'success' && (
            <>
              <div style={{ 
                fontSize: 'clamp(48px, 12vw, 64px)', 
                marginBottom: '16px',
                color: '#28a745'
              }}>
                ✓
              </div>
              <div style={{ 
                fontSize: 'clamp(22px, 6vw, 28px)', 
                fontWeight: 'bold',
                color: '#28a745',
                marginBottom: '12px'
              }}>
                Payment Successful!
              </div>
              <div style={{ 
                fontSize: 'clamp(14px, 4vw, 16px)', 
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Thank you for your payment. Your transaction has been completed successfully.
                <br />
                <br />
                You will receive a confirmation email shortly.
              </div>
              <div style={{
                padding: 'clamp(12px, 4vw, 20px)',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <div style={{ fontSize: 'clamp(13px, 3.5vw, 14px)', color: '#666', marginBottom: '8px' }}>
                  <strong>Invoice ID:</strong> <span style={{ wordBreak: 'break-all' }}>{invoice?.id}</span>
                </div>
                <div style={{ fontSize: 'clamp(13px, 3.5vw, 14px)', color: '#666', marginBottom: '8px' }}>
                  <strong>Patient:</strong> {invoice?.patientName}
                </div>
                <div style={{ fontSize: 'clamp(13px, 3.5vw, 14px)', color: '#666' }}>
                  <strong>Amount Paid:</strong> {formatCurrency(invoice?.total)}
                </div>
              </div>
              <div style={{ fontSize: 'clamp(12px, 3.5vw, 14px)', color: '#999' }}>
                You can safely close this page.
              </div>
            </>
          )}

          {paymentStatus === 'declined' && (
            <>
              <div style={{ 
                fontSize: 'clamp(48px, 12vw, 64px)', 
                marginBottom: '16px',
                color: '#dc3545'
              }}>
                ✗
              </div>
              <div style={{ 
                fontSize: 'clamp(22px, 6vw, 28px)', 
                fontWeight: 'bold',
                color: '#dc3545',
                marginBottom: '12px'
              }}>
                Payment Declined
              </div>
              <div style={{ 
                fontSize: 'clamp(14px, 4vw, 16px)', 
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Your payment was declined by your bank or card issuer.
                <br />
                <br />
                Please try a different payment method or contact your bank for more information.
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '14px 30px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  width: '100%',
                  maxWidth: '250px'
                }}
              >
                Try Again
              </button>
            </>
          )}

          {paymentStatus === 'error' && (
            <>
              <div style={{ 
                fontSize: 'clamp(48px, 12vw, 64px)', 
                marginBottom: '16px',
                color: '#ffc107'
              }}>
                ⚠
              </div>
              <div style={{ 
                fontSize: 'clamp(22px, 6vw, 28px)', 
                fontWeight: 'bold',
                color: '#ffc107',
                marginBottom: '12px'
              }}>
                Payment Error
              </div>
              <div style={{ 
                fontSize: 'clamp(14px, 4vw, 16px)', 
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                There was an error processing your payment.
                <br />
                <br />
                Please try again or contact support if the problem persists.
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '14px 30px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  width: '100%',
                  maxWidth: '250px'
                }}
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="public-invoice-container">
      <div className="public-invoice-card">
        {/* Header */}
        <div className="public-invoice-header">
          <h1>Invoice</h1>
          {invoice.practiceName && (
            <div style={{ fontSize: 'clamp(14px, 4vw, 16px)', opacity: '0.9' }}>
              From: {invoice.practiceName}
            </div>
          )}
        </div>

        {/* Invoice Content */}
        <div className="public-invoice-content">
          {/* Supersede status banners.
              -------------------------------------------------------
              Two distinct UX flows funnel through this hash:

              1. Staff (office manager) opens the invoice with
                 `?showOriginal=true` and the underlying invoice has
                 been superseded → render a yellow audit banner so
                 they know they're looking at the historical doc, not
                 what the patient sees.

              2. Patient opens any of the *patient-facing* SMS/email
                 links AFTER the office created a replacement → the
                 API quietly returned the latest invoice and set
                 `wasResolved=true`. Render a soft blue notice so a
                 confused patient who remembers a different amount
                 understands why this version differs. */}
          {showOriginal && invoice.status === 'superseded' && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffe082',
              color: '#7b5e00',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: 'clamp(13px, 3.5vw, 14px)',
            }}>
              <strong>Staff view:</strong> You are viewing the original (superseded) invoice. The
              patient receives the latest replacement automatically when they open their link.
            </div>
          )}
          {!showOriginal && invoice.wasResolved && (
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e3a8a',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: 'clamp(13px, 3.5vw, 14px)',
            }}>
              We've updated your invoice since the link was first sent. You're viewing the latest version.
            </div>
          )}

          {/* Invoice Info */}
          <div className="invoice-info-grid">
            <div>
              <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: 'clamp(16px, 4.5vw, 18px)' }}>Invoice Details</h3>
              {/* 2x2 grid of label/value pairs. Auto-fits to a single
                  column under ~280px so labels never collide on tiny
                  phones, but otherwise saves ~3 lines of vertical space
                  vs. the previous stacked layout. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                columnGap: '16px',
                rowGap: '12px',
                fontSize: 'clamp(13px, 3.5vw, 14px)'
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                    Invoice ID
                  </div>
                  <div style={{ wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' }}>
                    {invoice.id}
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                    Status
                  </div>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: getStatusStyle(invoice.status).bg,
                    color: getStatusStyle(invoice.status).color,
                    display: 'inline-block'
                  }}>
                    {getStatusStyle(invoice.status).label}
                  </span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                    Created
                  </div>
                  <div>{formatDate(invoice.createdAt)}</div>
                </div>
                {invoice.dueDate ? (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                      Due Date
                    </div>
                    <div>{formatDate(invoice.dueDate)}</div>
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: 'clamp(16px, 4.5vw, 18px)' }}>
                {invoice.sourceView === 'family' ? 'Family Information' : 'Patient Information'}
              </h3>
              <div style={{ fontSize: 'clamp(13px, 3.5vw, 14px)' }}>
                <strong>{invoice.sourceView === 'family' ? 'Account Holder:' : 'Patient Name:'}</strong>{' '}
                {invoice.patientName}
              </div>
              {/* Description rendered immediately under the patient
                  name so the "what is this charge for?" answer lives
                  with the "who" answer. Styled the same as the
                  Patient Name line above (plain inline label) — no
                  card / pill — to keep visual hierarchy flat. */}
              {invoice.description && (
                <div style={{
                  marginTop: '8px',
                  fontSize: 'clamp(13px, 3.5vw, 14px)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  <strong>Description:</strong> {invoice.description}
                </div>
              )}
              {/* When the bill was redirected from a dependent up to their
                  guarantor (Open Dental's Family Account model), make the
                  relationship explicit so the guarantor isn't surprised to
                  receive a charge they didn't personally incur. */}
              {invoice.billedToGuarantor && invoice.onBehalfOfName && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: '6px',
                    fontSize: 'clamp(12px, 3vw, 13px)',
                    color: '#3730a3',
                    display: 'inline-block',
                  }}
                >
                  Billed to <strong>{invoice.patientName}</strong> as guarantor
                  on behalf of <strong>{invoice.onBehalfOfName}</strong>
                </div>
              )}
            </div>
          </div>


          {/* Procedures Table */}
          {(() => {
            const procedures = invoice.lineItems?.filter(item => item.type === 'PROCEDURE') || [];
            const adjustments = invoice.lineItems?.filter(item => item.type === 'ADJUSTMENT') || [];
            const insurancePayments = invoice.lineItems?.filter(item => item.type === 'PAYMENT' && item.isInsurance) || [];
            const patientPayments = invoice.lineItems?.filter(item => item.type === 'PAYMENT' && !item.isInsurance) || [];
            // Carry-over rows from Ledger-tab invoices: a single line that
            // captures everything that happened BEFORE the activity window
            // we picked. Without rendering this, the visible line items
            // wouldn't add up to Balance Due.
            const carryovers = invoice.lineItems?.filter(item => item.type === 'CARRYOVER') || [];

            // Calculate totals from line items if summary not available
            const subtotalProcedures = invoice.summary?.subtotalProcedures ??
              procedures.reduce((sum, item) => sum + (item.price || 0), 0);
            const totalAdjustments = invoice.summary?.totalAdjustments ??
              adjustments.reduce((sum, item) => sum + (item.price || 0), 0);
            const totalInsurancePayments = invoice.summary?.totalInsurancePayments ??
              insurancePayments.reduce((sum, item) => sum + Math.abs(item.price || 0), 0);
            const totalPatientPayments = invoice.summary?.totalPatientPayments ??
              patientPayments.reduce((sum, item) => sum + Math.abs(item.price || 0), 0);
            // Previous balance: prefer the persisted summary value, fall
            // back to summing CARRYOVER line items so older invoices that
            // were saved before the summary fields existed still render.
            const previousBalance = invoice.summary?.previousBalance ??
              carryovers.reduce((sum, item) => sum + (item.price || 0), 0);
            
            return (
              <>
                {/* Previous Balance Section — chronologically the oldest
                    activity, so it sits at the top of the line items and
                    explains the gap between (procedures - payments) and
                    the actual Balance Due. */}
                {carryovers.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px', fontWeight: '600' }}>
                      PREVIOUS BALANCE
                    </h4>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f8fafc' }}>
                      {carryovers.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 15px',
                            borderBottom: index < carryovers.length - 1 ? '1px solid #e5e7eb' : 'none',
                            fontSize: '14px',
                            gap: '12px',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ color: '#374151', fontWeight: '500' }}>
                              {item.description || 'Previous balance brought forward'}
                            </span>
                            <span style={{ color: '#6b7280', fontSize: '12px' }}>
                              Net of all activity prior to this statement period
                            </span>
                          </div>
                          <div style={{
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            color: (item.price || 0) > 0 ? '#b91c1c' : (item.price || 0) < 0 ? '#0f5132' : '#212529',
                          }}>
                            {formatCurrency(item.price || 0)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Procedures Section */}
                {procedures.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 'clamp(16px, 4.5vw, 18px)' }}>
                      Services & Procedures
                    </h3>
                    
                    {/* Desktop Table */}
                    <div className="line-items-desktop" style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '90px 80px 2fr 100px',
                        gap: '10px',
                        padding: '12px 15px',
                        backgroundColor: '#f8f9fa',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        borderBottom: '1px solid #ddd'
                      }}>
                        <div>Date</div>
                        <div>Code</div>
                        <div>Description</div>
                        <div style={{ textAlign: 'right' }}>Amount</div>
                      </div>
                      {procedures.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '90px 80px 2fr 100px',
                            gap: '10px',
                            padding: '12px 15px',
                            borderBottom: index < procedures.length - 1 ? '1px solid #eee' : 'none',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ color: '#666' }}>
                            {formatLineItemDate(item.date)}
                          </div>
                          <div style={{ fontFamily: 'monospace', color: '#666' }}>{item.code || '-'}</div>
                          <div>{item.description}</div>
                          <div style={{ textAlign: 'right', fontWeight: '500', color: '#0f5132' }}>
                            {formatCurrency(item.price)}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="line-items-mobile">
                      {procedures.map((item, index) => (
                        <div key={index} className="line-item-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {formatLineItemDate(item.date)}
                            </span>
                            <span style={{ fontFamily: 'monospace', color: '#666', fontSize: '12px' }}>{item.code || ''}</span>
                          </div>
                          <div className="line-item-description">{item.description}</div>
                          <div style={{ textAlign: 'right', fontWeight: '600', color: '#0f5132' }}>
                            {formatCurrency(item.price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Adjustments Section */}
                {adjustments.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#856404', fontSize: '14px', fontWeight: '600' }}>
                      ADJUSTMENTS
                    </h4>
                    <div style={{ border: '1px solid #fff3cd', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fffbf0' }}>
                      {adjustments.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 15px',
                            borderBottom: index < adjustments.length - 1 ? '1px solid #fff3cd' : 'none',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {formatLineItemDate(item.date)}
                            </span>
                            <span>{item.description}</span>
                          </div>
                          <div style={{ fontWeight: '500', color: item.price < 0 ? '#28a745' : '#856404' }}>
                            {formatCurrency(item.price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Insurance Payments Section */}
                {insurancePayments.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#0066cc', fontSize: '14px', fontWeight: '600' }}>
                      INSURANCE PAYMENTS
                    </h4>
                    <div style={{ border: '1px solid #cce5ff', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f0f7ff' }}>
                      {insurancePayments.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 15px',
                            borderBottom: index < insurancePayments.length - 1 ? '1px solid #cce5ff' : 'none',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {formatLineItemDate(item.date)}
                            </span>
                            <span style={{ 
                              padding: '2px 6px', 
                              backgroundColor: '#e0f0ff', 
                              color: '#0066cc', 
                              borderRadius: '4px', 
                              fontSize: '11px' 
                            }}>Insurance</span>
                            <span style={{ color: '#666' }}>{item.description}</span>
                          </div>
                          <div style={{ fontWeight: '500', color: '#0066cc' }}>
                            {formatCurrency(Math.abs(item.price))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Patient Payments Section */}
                {patientPayments.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#004085', fontSize: '14px', fontWeight: '600' }}>
                      PATIENT PAYMENTS
                    </h4>
                    <div style={{ border: '1px solid #b8daff', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f8fbff' }}>
                      {patientPayments.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 15px',
                            borderBottom: index < patientPayments.length - 1 ? '1px solid #b8daff' : 'none',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {formatLineItemDate(item.date)}
                            </span>
                            <span style={{ 
                              padding: '2px 6px', 
                              backgroundColor: '#cce5ff', 
                              color: '#004085', 
                              borderRadius: '4px', 
                              fontSize: '11px' 
                            }}>Patient</span>
                            <span style={{ color: '#666' }}>{item.description}</span>
                          </div>
                          <div style={{ fontWeight: '500', color: '#004085' }}>
                            {formatCurrency(Math.abs(item.price))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary Totals */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '24px'
                }}>
                  <div style={{ 
                    width: '100%',
                    maxWidth: '320px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    border: '1px solid #ddd'
                  }}>
                    {Math.abs(previousBalance) >= 0.005 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' }}>
                        <span style={{ color: '#666' }}>Previous Balance:</span>
                        <span style={{
                          fontWeight: '500',
                          color: previousBalance > 0 ? '#b91c1c' : previousBalance < 0 ? '#0f5132' : '#212529',
                        }}>
                          {formatCurrency(previousBalance)}
                        </span>
                      </div>
                    )}
                    {procedures.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' }}>
                        <span style={{ color: '#666' }}>Subtotal (Procedures):</span>
                        <span style={{ fontWeight: '500' }}>{formatCurrency(subtotalProcedures)}</span>
                      </div>
                    )}
                    {adjustments.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' }}>
                        <span style={{ color: '#666' }}>Adjustments:</span>
                        <span style={{ fontWeight: '500', color: totalAdjustments < 0 ? '#28a745' : '#856404' }}>
                          {formatCurrency(totalAdjustments)}
                        </span>
                      </div>
                    )}
                    {totalInsurancePayments > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' }}>
                        <span style={{ color: '#666' }}>Insurance Payments:</span>
                        <span style={{ fontWeight: '500', color: '#0066cc' }}>
                          -{formatCurrency(totalInsurancePayments)}
                        </span>
                      </div>
                    )}
                    {totalPatientPayments > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' }}>
                        <span style={{ color: '#666' }}>Patient Payments:</span>
                        <span style={{ fontWeight: '500', color: '#004085' }}>
                          -{formatCurrency(totalPatientPayments)}
                        </span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 0 6px',
                      borderTop: '2px solid #333',
                      marginTop: '8px'
                    }}>
                      <span style={{ fontWeight: '700', fontSize: '16px' }}>Balance Due:</span>
                      <span style={{
                        fontWeight: '700',
                        fontSize: '20px',
                        color: invoice.total > 0 ? '#dc3545' : '#28a745'
                      }}>
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Payment Section - Only show for pending invoices */}
          {invoice.status === 'pending' && 
           (invoice.paymentMethodConfigId || invoice.payinConfigId) && (
            <div className="payment-section">
              <h3 style={{ 
                margin: '0 0 4px 0', 
                color: '#1976d2',
                textAlign: 'center',
                fontSize: 'clamp(18px, 5vw, 22px)'
              }}>
                Pay Your Invoice
              </h3>
              <div style={{
                margin: '0 0 16px 0',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: 'clamp(11px, 3vw, 13px)',
                letterSpacing: '0.02em'
              }}>
                Card &middot; HSA / FSA accepted
              </div>
              <div className="payment-container">
                 {/* Show loading state while session is being fetched */}
                 {sessionLoading && !sessionKey && (
                   <div style={{
                     textAlign: 'center',
                     color: '#666',
                     fontSize: 'clamp(14px, 4vw, 16px)',
                     padding: 'clamp(24px, 6vw, 40px)'
                   }}>
                     <div style={{
                       display: 'inline-block',
                       width: '32px',
                       height: '32px',
                       border: '3px solid #f3f3f3',
                       borderTop: '3px solid #1976d2',
                       borderRadius: '50%',
                       animation: 'spin 1s linear infinite',
                       marginBottom: '12px'
                     }} />
                     <div>Loading payment form...</div>
                   </div>
                 )}
                 
                 {/* Show error if session failed to load */}
                 {!sessionLoading && !sessionKey && (
                   <div style={{
                     textAlign: 'center',
                     color: '#dc3545',
                     fontSize: 'clamp(14px, 4vw, 16px)',
                     padding: 'clamp(24px, 6vw, 40px)',
                     lineHeight: '1.5'
                   }}>
                     Unable to load payment form. Please refresh the page to try again.
                   </div>
                 )}
                 
                {/* ---------------------------------------------------------
                    DOB-gated saved-card management.

                    Layered ABOVE the existing Rainforest payment widget
                    rather than replacing it: a patient who never opts
                    into the gate sees the original guest-pay UX, while
                    one who verifies their DOB unlocks the saved-card
                    list / add-card form. When KollaContacts has no
                    birth_date (gateUnavailable === true) the entry CTA
                    is suppressed entirely and the page silently behaves
                    as before.
                    --------------------------------------------------------- */}
                {!gateUnavailable && gateView === 'idle' && !gateToken && (
                  <button
                    type="button"
                    onClick={() => setGateView('dob')}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 14px',
                      marginBottom: 16,
                      backgroundColor: '#fff',
                      color: '#1976d2',
                      border: '1px solid #1976d2',
                      borderRadius: 6,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Use a saved card on this invoice
                  </button>
                )}

                {gateView === 'dob' && (
                  <DobGate
                    publicHash={hash}
                    onVerified={handleDobVerified}
                    onUnavailable={handleDobUnavailable}
                    onCancel={() => setGateView('idle')}
                  />
                )}

                {gateToken && (gateView === 'list' || gateView === 'add') && (
                  <>
                    {gateMeta?.firstName && (
                      <div style={{
                        fontSize: 14,
                        color: '#586473',
                        marginBottom: 8,
                      }}>
                        Welcome back, {gateMeta.firstName}.
                      </div>
                    )}

                    {gateView === 'list' && (
                      <>
                        {loadingSavedMethods && (
                          <div style={{ color: '#586473', fontSize: 13, marginBottom: 8 }}>
                            Loading your saved cards…
                          </div>
                        )}
                        {savedMethodsError && (
                          <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 8 }}>
                            {savedMethodsError}
                          </div>
                        )}
                        <SavedCardList
                          publicHash={hash}
                          gateToken={gateToken}
                          methods={savedMethods}
                          onAddNew={() => setGateView('add')}
                          onChanged={loadSavedMethods}
                          onChargeCardOnFile={handleChargeSavedCard}
                          chargingVaultId={chargingVaultId}
                        />
                        {chargeError && (
                          <div style={{
                            color: '#b3261e',
                            fontSize: 13,
                            marginTop: 8,
                            padding: '8px 12px',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 6,
                          }}>
                            {chargeError}
                          </div>
                        )}
                      </>
                    )}

                    {gateView === 'add' && (
                      <AddPaymentMethod
                        publicHash={hash}
                        gateToken={gateToken}
                        makeDefault={makeNewCardDefault}
                        setMakeDefault={setMakeNewCardDefault}
                        hasExistingCards={savedMethods.length > 0}
                        onSaved={handleAddCardSaved}
                        onCancel={() => setGateView('list')}
                      />
                    )}
                  </>
                )}

                {/* Rainforest Payment Component - only render when we have sessionKey.
                    Suppress while the patient is in the saved-card add flow so the
                    page doesn't show two stacked card forms (the AddPaymentMethod
                    one for tokenization plus this main payin one). */}
                {sessionKey && gateView !== 'add' && (
                  <>
                    {!paymentFormLoaded && (
                      <div style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: 'clamp(14px, 4vw, 16px)',
                        marginBottom: '16px'
                      }}>
                        Loading payment form...
                      </div>
                    )}

                    {/* ------------------------------------------------------
                        Surcharge UX (Option-2: upfront notice + post-BIN
                        dynamic update).
                        ------------------------------------------------------
                        Mounted only when `invoice.surchargeNotice` is
                        present (server-gated on the practice opting in).

                        Two stacked panels, designed to give the patient
                        zero-surprise pricing:
                          1. Upfront banner — "Credit cards add up to X%"
                             — sets expectations BEFORE they touch the
                             form. Always visible while surcharging is on.
                          2. Dynamic breakdown — appears after the first
                             bin-lookup. Shows the actual surcharge
                             ($Z.YY) for credit cards or "no surcharge"
                             for debit/prepaid/HSA. Replaces the upfront
                             estimate with the real number.
                        ------------------------------------------------------ */}
                    {surchargingMode && (
                      <div style={{ marginBottom: 16 }}>
                        {/* Upfront banner — only visible BEFORE the
                            first card classification. Once a card has
                            been classified (surcharge or no surcharge)
                            this hides and the result panel takes over,
                            so the patient never sees both "may apply"
                            and a definitive answer at the same time. */}
                        {!quoteReceived && !quoteLoading && !quoteError && (
                          <div style={{
                            backgroundColor: '#fff8e1',
                            border: '1px solid #ffe082',
                            borderRadius: 8,
                            padding: '12px 14px',
                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                            color: '#7b5e00',
                            marginBottom: 12,
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              Heads up: credit-card surcharge may apply
                            </div>
                            <div style={{ lineHeight: 1.4 }}>
                              {invoice.surchargeNotice.disclosure}
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12, color: '#9a7100' }}>
                              Subtotal {formatCurrency(invoice.total)} ·
                              {' '}
                              up to {formatCurrency(
                                (invoice.surchargeNotice.exampleTotalCents || 0) / 100,
                              )} with credit
                            </div>
                          </div>
                        )}

                        {/* Result panel renders only when there's
                            actually something to say: a surcharge to
                            display, an in-flight quote, or an error.
                            Pure no-surcharge results (debit/HSA) hide
                            the panel entirely so the patient sees a
                            clean form, not a colored container with
                            nothing inside. */}
                        {(surchargeQuote || quoteLoading || quoteError) && (
                          <div style={{
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #90caf9',
                            borderRadius: 8,
                            padding: '12px 14px',
                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                            color: '#0d47a1',
                          }}>
                            {quoteLoading && (
                              <div style={{ color: '#666', fontSize: 13 }}>
                                Updating total based on your card…
                              </div>
                            )}
                            {!quoteLoading && quoteError && (
                              <div style={{ color: '#b3261e', fontSize: 13 }}>
                                {quoteError}
                              </div>
                            )}
                            {!quoteLoading && !quoteError && surchargeQuote && (
                              <>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                  Updated total
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: 13,
                                  marginBottom: 2,
                                }}>
                                  <span>Subtotal</span>
                                  <span>{formatCurrency(
                                    (surchargeQuote.totalCents - surchargeQuote.surchargeCents) / 100,
                                  )}</span>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: 13,
                                  marginBottom: 2,
                                }}>
                                  <span>Credit-card surcharge ({surchargeQuote.surchargePct}%)</span>
                                  <span>+{formatCurrency(surchargeQuote.surchargeCents / 100)}</span>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: 15,
                                  fontWeight: 700,
                                  marginTop: 6,
                                  paddingTop: 6,
                                  borderTop: '1px solid #bbdefb',
                                }}>
                                  <span>Total</span>
                                  <span>{formatCurrency(surchargeQuote.totalCents / 100)}</span>
                                </div>
                                {surchargeQuote.disclosure && (
                                  <div style={{
                                    color: '#1565c0',
                                    fontSize: 11,
                                    marginTop: 6,
                                    lineHeight: 1.4,
                                  }}>
                                    {surchargeQuote.disclosure}
                                  </div>
                                )}
                              </>
                            )}
                            {/* No-surcharge case (debit/HSA/prepaid):
                                deliberately render NOTHING here. The
                                Pay button below already shows the
                                correct total and the upfront banner is
                                already hidden — adding "✓ No surcharge
                                applies / Total $100.00" would just be
                                redundant noise next to a form whose
                                price already matches the invoice. */}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Allowed-methods derivation
                        ----------------------------
                        Two policy layers stack to produce the final
                        list passed to <rainforest-payment>:

                          (A) Per-practice opt-in flags (SettingsTab):
                              ``paypalWalletEnabled`` / ``venmoEnabled``
                              / ``paypalPayLaterEnabled``. Each defaults
                              to false — the practice must explicitly
                              accept the PayPal-network processing rate
                              (2.99% / 3.49% / 4.99% + $0.49).

                          (B) Surcharging override: when the practice
                              has credit-card surcharging on we force
                              CARD-only, no matter what the flags say.
                              Reason: PayPal's merchant agreement forbids
                              surcharging PayPal regardless of underlying
                              funding source. Showing a fee-free PayPal
                              lane alongside a 3%-surcharged card lane
                              would let patients trivially route around
                              the surcharge and defeat the practice's
                              cost-recovery opt-in.

                        For surcharging practices we therefore stay on
                        the existing CARD-only flow (hide-button +
                        custom React submit that fires after the BIN-
                        lookup config swap). For non-surcharging
                        practices we light up the new per-method
                        buttons UI with whichever PayPal-family methods
                        the practice has enabled, alongside CARD.

                        ``ALLOWED_METHOD_BUTTONS`` is only switched on
                        when at least one PayPal-family method is
                        actually allowed — for a CARD-only practice we
                        keep the legacy embedded card form (the new
                        buttons UI for just CARD is uglier than the
                        plain form).

                        Apple Pay still disabled per the pre-existing
                        note. */}
                    {(() => {
                      const allowed = ['CARD'];
                      if (!surchargingMode) {
                        if (invoice.paypalWalletEnabled) allowed.push('PAYPAL_WALLET');
                        if (invoice.venmoEnabled) allowed.push('VENMO');
                        if (invoice.paypalPayLaterEnabled) allowed.push('PAYPAL_PAYLATER');
                      }
                      const hasPayPalFamily = allowed.length > 1;
                      return (
                        <rainforest-payment
                          ref={paymentComponentRef}
                          session-key={sessionKey}
                          payin-config-id={currentPayinConfigId || invoice.payinConfigId}
                          allowed-methods={allowed.join(',')}
                          preview-features={hasPayPalFamily ? 'ALLOWED_METHOD_BUTTONS' : undefined}
                          // Empty string == attribute present (HTML boolean attr
                          // semantics). Undefined causes React to omit the
                          // attribute entirely so non-surcharging practices keep
                          // the built-in pay button.
                          hide-button={surchargingMode ? '' : undefined}
                        ></rainforest-payment>
                      );
                    })()}

                    {/* Custom submit button — only when we've hidden
                        Rainforest's built-in. Three disabled states,
                        each with its own copy so the patient knows
                        exactly why the button isn't responsive:
                          * configSwapPending → "Updating total…"
                          * submitInFlight    → "Processing payment…"
                                                + spinner
                          * default           → "Pay $X.XX"
                        ``submitInFlight`` AND the synchronous
                        ``submitInFlightRef`` guard inside
                        handleCustomSubmit both block double-clicks
                        — the disabled attribute is the visual
                        affordance, the ref is the correctness
                        guarantee. */}
                    {surchargingMode && (() => {
                      const buttonDisabled = (
                        configSwapPending || quoteLoading || submitInFlight
                      );
                      return (
                        <button
                          type="button"
                          onClick={handleCustomSubmit}
                          disabled={buttonDisabled}
                          aria-busy={submitInFlight ? 'true' : 'false'}
                          style={{
                            marginTop: 16,
                            width: '100%',
                            padding: '14px 18px',
                            backgroundColor: buttonDisabled ? '#90caf9' : '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: buttonDisabled ? 'wait' : 'pointer',
                            transition: 'background-color 150ms ease-out',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 10,
                          }}
                        >
                          {submitInFlight && (
                            <span
                              aria-hidden="true"
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.4)',
                                borderTopColor: '#ffffff',
                                // ``spin`` is the global @keyframes injected
                                // via responsiveStyles at the top of this file;
                                // reused by every loading affordance on the page.
                                animation: 'spin 0.8s linear infinite',
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>
                            {submitInFlight
                              ? 'Processing payment…'
                              : configSwapPending
                                ? 'Updating total…'
                                : surchargeQuote
                                  ? `Pay ${formatCurrency(surchargeQuote.totalCents / 100)}`
                                  : `Pay ${formatCurrency(invoice.total)}`}
                          </span>
                        </button>
                      );
                    })()}

                    {/* Opportunistic-save checkbox.
                        Shown alongside the main payin form regardless of
                        gate-verification state — a verified patient who
                        hasn't saved a card yet (or wants to save a
                        different one during checkout) should still be
                        able to flip this in one click. The explicit
                        SavedCardList / AddPaymentMethod path is just an
                        alternative entry point; nothing about it makes
                        the inline opportunistic-save redundant here. */}
                    {(
                      <div style={{
                        marginTop: 12,
                        padding: '12px 14px',
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          cursor: saveCardSyncing ? 'wait' : 'pointer',
                          margin: 0,
                          opacity: saveCardSyncing ? 0.7 : 1,
                        }}>
                          <input
                            type="checkbox"
                            checked={saveCardOnPay}
                            disabled={saveCardSyncing}
                            onChange={handleToggleSaveOnPay}
                            style={{
                              flexShrink: 0,
                              width: 16,
                              height: 16,
                              margin: 0,
                              marginTop: 3,
                              accentColor: '#1976d2',
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                            <div style={{
                              fontWeight: 500,
                              color: '#333',
                              lineHeight: 1.4,
                              fontSize: 'clamp(13px, 3.6vw, 14px)',
                            }}>
                              Save my card for next time
                            </div>
                            <div style={{
                              color: '#6c757d',
                              fontSize: 'clamp(11px, 3vw, 12px)',
                              marginTop: 2,
                              lineHeight: 1.4,
                            }}>
                              Securely store this card with our payment processor so
                              you can pay future invoices in one tap.
                            </div>
                          </div>
                        </label>
                        {saveCardError && (
                          <div style={{
                            color: '#dc3545',
                            fontSize: 12,
                            marginTop: 8,
                          }}>
                            {saveCardError}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
               </div>
            </div>
          )}

          {/* Payment Status Message for non-pending invoices */}
          {invoice.status !== 'pending' && (
            <div className="status-message-box" style={{
              backgroundColor: getStatusStyle(invoice.status).bg,
              color: getStatusStyle(invoice.status).color,
              padding: 'clamp(16px, 4vw, 20px)',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center',
              border: `1px solid ${getStatusStyle(invoice.status).color}20`
            }}>
              <div style={{ fontSize: 'clamp(16px, 4.5vw, 18px)', fontWeight: 'bold', marginBottom: '8px' }}>
                {invoice.status === 'paid' && '✅ Payment Complete'}
                {invoice.status === 'failed' && '❌ Payment Failed'}
                {invoice.status === 'canceled' && '🚫 Payment Canceled'}
                {invoice.status === 'returned' && '↩️ Payment Returned'}
                {invoice.status === 'authorized' && '✓ Payment Authorized'}
                {invoice.status === 'processing' && '⏳ Payment Processing'}
                {invoice.status === 'under_review' && '🔍 Payment Under Review'}
                {invoice.status?.startsWith('chargeback_') && '⚠️ Chargeback in Progress'}
                {!['paid', 'failed', 'canceled', 'returned', 'authorized', 'processing', 'under_review'].includes(invoice.status) && 
                 !invoice.status?.startsWith('chargeback_') &&
                 `📋 Status: ${getStatusStyle(invoice.status).label}`}
              </div>
              <div style={{ fontSize: 'clamp(13px, 3.5vw, 14px)', lineHeight: '1.5' }}>
                {invoice.status === 'paid' && 'Thank you for your payment. This invoice has been paid in full.'}
                {invoice.status === 'failed' && 'The payment for this invoice failed. Please contact the practice for assistance.'}
                {invoice.status === 'canceled' && 'The payment for this invoice was canceled.'}
                {invoice.status === 'returned' && 'The payment for this invoice was returned by your bank. Please contact the practice for assistance.'}
                {invoice.status === 'authorized' && 'Your payment has been authorized and is being processed.'}
                {invoice.status === 'processing' && 'Your payment is currently being processed. You will receive confirmation once complete.'}
                {invoice.status === 'under_review' && 'Your payment is under review for security purposes. This is normal and will be resolved soon.'}
                {invoice.status?.startsWith('chargeback_') && 'A chargeback has been initiated for this payment. Please contact the practice for more information.'}
                {!['paid', 'failed', 'canceled', 'returned', 'authorized', 'processing', 'under_review'].includes(invoice.status) && 
                 !invoice.status?.startsWith('chargeback_') &&
                 'Payment is not available for this invoice at this time.'}
              </div>
            </div>
          )}

          {/* Footer Message */}
          <div className="footer-message" style={{
            textAlign: 'center',
            padding: 'clamp(16px, 4vw, 20px)',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: 'clamp(13px, 3.5vw, 14px)',
            color: '#666',
            lineHeight: '1.5'
          }}>
            <div style={{ marginBottom: '8px', fontWeight: '500' }}>
              Thank you for your business!
            </div>
            <div>
              If you have any questions about this invoice, please contact the practice directly.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicInvoice;
